import { createReducer } from '@reduxjs/toolkit';
import type { Draft, PayloadAction } from '@reduxjs/toolkit';
import * as _ from 'lodash-es';

import { PART_DELETE_SUCCESS } from 'vms/features/parts/actions';
import type { Part } from 'vms/features/parts/types';

import * as Actions from './actions';
import type {
  InspectionCategory,
  InspectionTask,
  InspectionTaskResult,
  InspectionTaskNote,
} from './types';

export interface State {
  categories: { [id: string]: InspectionCategory };
  tasks: { [id: string]: InspectionTask };
  results: { [id: string]: InspectionTaskResult };
}

export const initialState: State = {
  categories: {},
  tasks: {},
  results: {},
};

type LoadInspectionCategoriesSuccessAction = PayloadAction<
  { categories: InspectionCategory[] },
  typeof Actions.LOAD_INSPECTION_CATEGORIES_SUCCESS
>;
type LoadInspectionTasksSuccessAction = PayloadAction<
  { tasks: InspectionTask[] },
  typeof Actions.LOAD_INSPECTION_TASKS_SUCCESS
>;
type LoadInspectionResultsSuccessAction = PayloadAction<
  { results: InspectionTaskResult[] },
  typeof Actions.LOAD_INSPECTION_RESULT_SUCCESS
>;
type UpdateInspectionResultsAction = PayloadAction<
  { result: InspectionTaskResult },
  typeof Actions.UPDATE_INSPECTION_RESULT
>;
type InspectionTaskNoteCreatedAction = PayloadAction<
  { note: InspectionTaskNote },
  typeof Actions.INSPECTION_TASK_NOTE_CREATED
>;
type InspectionResultAddPartAction = PayloadAction<
  { part: Part; inspectionResultId: string },
  typeof Actions.INSPECTION_RESULT_ADD_PART
>;
type AddInspectionTaskSuccessAction = PayloadAction<
  { task: InspectionTask },
  typeof Actions.ADD_INSPECTION_TASK_SUCCESS
>;
type PartDeleteSuccessAction = PayloadAction<{ part: Part }, typeof PART_DELETE_SUCCESS>;

const loadInspectionCategoriesSuccess = (
  state: Draft<State>,
  action: LoadInspectionCategoriesSuccessAction
) => {
  state.categories = {
    ...state.categories,
    ..._.keyBy(action.payload.categories, 'id'),
  };
};

const loadInspectionTasksSuccess = (
  state: Draft<State>,
  action: LoadInspectionTasksSuccessAction
) => {
  state.tasks = { ...state.tasks, ..._.keyBy(action.payload.tasks, 'id') };
};

const addInspectionTaskSuccess = (
  state: Draft<State>,
  action: AddInspectionTaskSuccessAction
) => {
  const { task } = action.payload;
  state.tasks = { ...state.tasks, [task.id]: task };
  state.categories[task.category].tasks.push(task.id);
};

const loadInspectionResultsSuccess = (
  state: Draft<State>,
  action: LoadInspectionResultsSuccessAction
) => {
  state.results = { ...state.results, ..._.keyBy(action.payload.results, 'id') };
};

const updateInspectionResults = (
  state: Draft<State>,
  action: UpdateInspectionResultsAction
) => {
  state.results[action.payload.result.id] = action.payload.result;
};

const inspectionTaskNoteCreated = (
  state: Draft<State>,
  action: InspectionTaskNoteCreatedAction
) => {
  const result = state.results[action.payload.note.inspection_result];
  if (result) {
    result.notes.push(action.payload.note.text);
  }
};

const inspectionResultAddPart = (
  state: Draft<State>,
  action: InspectionResultAddPartAction
) => {
  const { part, inspectionResultId } = action.payload;
  if (!state.results[inspectionResultId]) {
    return;
  }
  const parts = state.results[inspectionResultId].parts;
  for (let i = 0; i < parts.length; i++) {
    if (parts[i].id === part.id) {
      parts[i] = part;
      return;
    }
  }
  parts.push(part);
};

const inspectionResultDeletePart = (
  state: Draft<State>,
  action: PartDeleteSuccessAction
) => {
  const { part } = action.payload;
  if (!state.results[part.inspection_result]) {
    return;
  }
  const result = state.results[part.inspection_result];
  result.parts = result.parts.filter((p) => p.id !== part.id);
};

export const reducer = createReducer(initialState, (builder) => {
  builder.addCase(
    Actions.LOAD_INSPECTION_CATEGORIES_SUCCESS,
    loadInspectionCategoriesSuccess
  );
  builder.addCase(Actions.LOAD_INSPECTION_TASKS_SUCCESS, loadInspectionTasksSuccess);
  builder.addCase(Actions.LOAD_INSPECTION_RESULT_SUCCESS, loadInspectionResultsSuccess);
  builder.addCase(Actions.UPDATE_INSPECTION_RESULT, updateInspectionResults);
  builder.addCase(Actions.INSPECTION_TASK_NOTE_CREATED, inspectionTaskNoteCreated);
  builder.addCase(Actions.INSPECTION_RESULT_ADD_PART, inspectionResultAddPart);
  builder.addCase(Actions.ADD_INSPECTION_TASK_SUCCESS, addInspectionTaskSuccess);
  builder.addCase(PART_DELETE_SUCCESS, inspectionResultDeletePart);
});
